// Build's durable + view models (Slice 5; slice-plan.md:79-81; D34 app-stage lifecycle + portable builds).
//
// Build turns an approved, validated idea into a portable MVP and then a hardened Production app. Two
// load-bearing invariants are STRUCTURAL (app-engineering §2: illegal states do not compile):
//   - THE STAGE LADDER IS FORWARD-ONLY (D34). `StageTransition` is a discriminated union whose ONLY
//     members are none->validated, validated->mvp, mvp->production. A reverse (production->mvp) or skip
//     (none->production) transition is UNREPRESENTABLE - the pair is not a union member.
//   - THE HARD GATE IS A STRUCTURAL RESULT (§0 #1; slice-plan.md:81). `BuildResult` cannot present a
//     `built` app while a hard gate (payments/auth/pii) is pending: `awaiting_hard_gate` and `built`
//     are distinct union members, so "built AND awaiting the hard gate" cannot be represented.
//
// `StageReadiness` is DERIVED, never stored (app-engineering §2 "keep derived state acyclic"): it reads
// narrower state (stage, verdict, approval gate, workflow validation) and returns the booleans, so it is
// recomputed, never persisted-and-trusted. No cost/entitlement figure is invented here (§8).
import { z } from "zod";
import { AppId } from "../ids.js";
import { NonEmptyString, Timestamp } from "./common.js";
import { EvidencePointer } from "./certify.js";
import { AppStage, Cloud, EnvironmentType, PlanName } from "./enums.js";
/**
 * DERIVED build readiness for an app (D34). NEVER stored - computed from the app's current stage,
 * verdict, approval gate, and workflow validation, so it cannot go stale against its source. `blockers`
 * names what stands in the way (empty when ready).
 */
export const StageReadiness = z.object({
    canBuildMvp: z.boolean(),
    canPromoteProduction: z.boolean(),
    blockers: z.array(NonEmptyString),
});
/**
 * THE FORWARD-ONLY STAGE LADDER (D34). A discriminated union on the target stage `to`, whose ONLY
 * members are the two legal forward hops between the real `AppStage` values (enums.ts:34):
 * validated->mvp and mvp->production. There is NO `none` origin - `stage` is non-nullable and every app
 * is at least `validated`, so a `from:"none"` edge is not a value (D4 reconciliation). Because each
 * member fixes BOTH `from` and `to` as literals, a reverse (production->mvp) or skip
 * (validated->production) transition routes to the member with the matching `to` and then FAILS its
 * `from` literal - so an illegal promotion cannot be constructed as a value.
 */
export const StageTransition = z.discriminatedUnion("to", [
    z.strictObject({ from: z.literal("validated"), to: z.literal("mvp") }),
    z.strictObject({ from: z.literal("mvp"), to: z.literal("production") }),
]);
/** Which build stage is being run. A subset of `AppStage` (D34): only mvp and production are BUILT. */
export const BuildStageKind = z.enum(["mvp", "production"]);
/**
 * The build request (D34; slice-plan.md:79). `cloud` REUSES the existing `Cloud` enum (enums.ts:82) -
 * the founder picks managed or a production cloud here. `appId` is carried in the body because the
 * BuildOrchestrator port needs it; the ROUTE path `:appId` is authoritative on any conflict (the server
 * scopes the write to the path's app, never the body's).
 */
export const BuildRequest = z.object({
    appId: AppId,
    stage: BuildStageKind,
    cloud: Cloud,
});
/**
 * The portable stack a build produces (D34; slice-plan.md:79: "containers, standard Postgres,
 * Terraform"). `postgres` is LITERALLY `standard` - a portable MVP never binds to a proprietary managed
 * database, so a non-standard Postgres cannot be represented in the produced stack.
 */
export const PortableStackSpec = z.object({
    containers: z.array(NonEmptyString),
    postgres: z.literal("standard"),
    terraformRef: NonEmptyString,
});
/** The status of one hardening-checklist item (Production build, slice-plan.md:80). */
export const HardeningItemStatus = z.enum(["pass", "fail", "na"]);
/** One hardening-checklist item, carrying dated evidence (reuses certify's `EvidencePointer`). */
export const HardeningItem = z.object({
    key: NonEmptyString,
    status: HardeningItemStatus,
    evidence: EvidencePointer,
});
/** The Production hardening checklist (slice-plan.md:80). Every item is grounded by an EvidencePointer. */
export const HardeningChecklist = z.object({
    items: z.array(HardeningItem),
});
/** One rung of the environment ladder cleared before Production promotion (D23 env ladder; slice-plan.md:80). */
export const EnvLadderStep = z.object({
    env: EnvironmentType,
    cleared: z.boolean(),
});
/** A hard gate a build must clear first (§0 #1): the founder's absolute gate on payments/auth/PII. */
export const HardGateReason = z.enum(["payments", "auth", "pii"]);
/** The kind of a build step (data-model.md:54 build_steps.kind). A multi-class chain orders through these. */
export const BuildStepKind = z.enum(["compile", "sign", "test", "package", "distribute", "manual"]);
/** A build step's run state (data-model.md:54 build_steps.state). `awaiting_manual_step` pauses for the founder (D64). */
export const BuildStepState = z.enum([
    "pending",
    "running",
    "awaiting_manual_step",
    "verifying",
    "done",
    "failed",
]);
/**
 * A CUSTOMER-facing projection of one build step for the polled progress view (D3). STRICT and
 * DELIBERATELY MINIMAL: it carries the id, kind, label, and state ONLY. It MUST NOT carry `runner_class`
 * or any cost/spend field (D27; §0 #6 - no cost surface to a customer path) - mirrors how `AppView`
 * (model/apps.ts:69) omits the spend fields. `strictObject` makes a smuggled `runnerClass`/`cost` key a
 * PARSE ERROR, so the internal field cannot leak into the customer projection by accident.
 */
export const BuildStepView = z.strictObject({
    id: NonEmptyString,
    kind: BuildStepKind,
    label: NonEmptyString,
    state: BuildStepState,
});
/**
 * THE BUILD RESULT (§0 #1; slice-plan.md:81). A discriminated union on `state`:
 *   - `awaiting_hard_gate`: a hard gate (payments/auth/pii) must clear first (§0 #1). NO built app.
 *   - `awaiting_manual_step`: the build paused at a manual step (D64; Slice 18), carrying its id.
 *   - `refused_entitlement`: the plan's tier does not entitle this build (D65), carrying the tier needed.
 *   - `building`: the build is in progress; carries the buildId and the customer step projections (D3).
 *   - `built`: the portable stack, the preview, and the NEW app stage.
 * "built while awaiting the hard gate" is UNREPRESENTABLE - the states are distinct, strict members, so
 * a success payload cannot smuggle a built app past a pending gate.
 */
export const BuildResult = z.discriminatedUnion("state", [
    z.strictObject({ state: z.literal("awaiting_hard_gate"), reason: HardGateReason }),
    z.strictObject({ state: z.literal("awaiting_manual_step"), manualStepId: NonEmptyString }),
    z.strictObject({ state: z.literal("refused_entitlement"), requiredTier: PlanName }),
    z.strictObject({
        state: z.literal("building"),
        buildId: NonEmptyString,
        steps: z.array(BuildStepView),
    }),
    z.strictObject({
        state: z.literal("built"),
        stackSpec: PortableStackSpec,
        previewUrl: z.url(),
        newStage: AppStage,
    }),
]);
// --- Guided workflow validation before build (D35; slice-plan.md:78; moved here in the D5 reconciliation) ---
/** One workflow step Glim validates with the founder before a build stage (D35). */
export const WorkflowStep = z.object({
    id: NonEmptyString,
    label: NonEmptyString,
    confirmed: z.boolean(),
});
/**
 * The guided workflow validation (D35): a BUILD-PHASE gate where Glim walks the founder through the
 * app's OWN workflows to confirm scope BEFORE building, preventing rework. `steps` is the checklist;
 * the build lane reads whether every step is confirmed. `validatedAt` is null until the founder
 * completes the walkthrough.
 */
export const WorkflowValidation = z.object({
    steps: z.array(WorkflowStep),
    validatedAt: Timestamp.nullable(),
});
//# sourceMappingURL=build.js.map